import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Activity, Play, AlertTriangle, History, BookOpen, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const DOT = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-red-500", info: "bg-slate-400" };
const LBL = { green: "OK", yellow: "Da aggiornare", red: "Problema", info: "Manuale" };

// Descrizione della funzione mostrata sotto ogni semaforo.
const DESC = {
  commesse: "Riscarica l'elenco completo delle commesse (entrambe le divisioni) con consegne, pagamenti ed esposizione/affidamento, e aggiorna il match ordine↔commessa. Non apre i dettagli articolo. Automatico: ogni notte (02:00) e ogni ora (:30).",
  dettaglio: "Apre il dettaglio di ogni commessa aperta e calcola Consegnata/Assegnata per articolo e i movimenti di uscita (impegni). Manuale: incrementale (le commesse invariate vengono saltate). Automatico ogni notte (21:40): verifica COMPLETA di TUTTE le commesse (intercetta cambi di date/dettagli anche sulle esistenti).",
  giacenza: "Scarica il magazzino fiscale (CSV), fonte autorevole della giacenza a magazzino, e ricalcola il disponibile con gli impegni esistenti. Veloce (pochi secondi). Automatico: ogni notte (21:40).",
  ordini: "Sincronizza ordini e preventivi dal gestionale (incrementale) e ricalcola la profilazione. Automatico: ogni ora (:30).",
  listino: "Listino prezzi/prodotti usato per riconoscere gli articoli nelle commesse e negli ordini. Import manuale.",
};

// Semafori serviti dal job magazzino (dettaglio articoli e giacenza CSV, ora separati).
const MAG_KEYS = new Set(["dettaglio", "giacenza"]);

// Per ogni semaforo: quale funzione lanciare manualmente (e come seguirne il completamento).
const TRIGGER = {
  commesse: { url: "/admin/sync-commesse", title: "Riscarica elenco commesse" },
  dettaglio: { url: "/magazzino/sync/dettaglio", title: "Ricostruisci SOLO il dettaglio articoli / impegni (lento)" },
  giacenza: { url: "/magazzino/sync/giacenza", title: "Ricarica SOLO la giacenza dal CSV fiscale (veloce)" },
  ordini: { url: "/import/fier-vendite-start", body: {}, statusUrl: "/import/fier-vendite-status", title: "Sincronizza ordini e preventivi" },
};

// Conferma richiesta prima dell'avvio manuale (operazioni lente), per evitare avvii accidentali.
const CONFIRM = {
  dettaglio: "Il Dettaglio articoli è un'operazione LENTA (~10-15 min, gira in background).\n\nGira già in automatico ogni notte alle 21:40. Vuoi avviarla comunque adesso?",
};

const fmt = (iso) => {
  if (!iso) return "mai";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const Bar = ({ pct }) => (
  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted" data-testid="sync-bar">
    {pct == null
      ? <div className="h-full w-1/3 animate-pulse rounded-full bg-amber-500" />
      : <div className="h-full rounded-full bg-amber-500 transition-all duration-500" style={{ width: `${Math.min(100, Math.max(4, pct))}%` }} />}
  </div>
);

export default function SyncStatusPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [mag, setMag] = useState(null);         // stato live del job magazzino (/magazzino/status)
  const [busyKey, setBusyKey] = useState(null); // trigger non-magazzino in corso (commesse/ordini)
  const [val, setVal] = useState(null);
  const [istrOpen, setIstrOpen] = useState(false);

  const refresh = useCallback(() => {
    api.get("/magazzino/sync-status").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { api.get("/magazzino/valore").then((r) => setVal(r.data)).catch(() => {}); }, []);

  // Polling continuo dello stato del job magazzino: unico indicatore, indipendente da chi l'ha avviato.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get("/magazzino/status");
        if (!alive) return;
        setMag(r.data);
        if (wasRunning.current && !r.data?.running) refresh(); // appena finito → aggiorna i semafori
        wasRunning.current = !!r.data?.running;
      } catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, [isAdmin, refresh]);

  const magRunning = !!mag?.running;

  const caricaNottePrima = async () => {
    if (magRunning) return;
    if (!window.confirm("Carico la giacenza dallo snapshot CSV della NOTTE PRIMA?\n\nSostituisce la giacenza corrente e ricalcola la disponibilità (ATP). Usalo se il magazzino di oggi è errato.")) return;
    try {
      await api.post("/magazzino/sync/giacenza-precedente", {});
      setMag((m) => ({ ...(m || {}), running: true, job: "giacenza", updated: 0, total: 0, commesse_done: 0, commesse_total: 0 }));
      wasRunning.current = true;
      toast.info("Carico la giacenza della notte prima…");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Avvio fallito");
    }
  };

  const pollVendite = useCallback((statusUrl) => {
    api.get(statusUrl).then((r) => {
      if (r.data?.running) { setTimeout(() => pollVendite(statusUrl), 4000); }
      else { setBusyKey(null); refresh(); toast.success("Sincronizzazione completata"); }
    }).catch(() => { setBusyKey(null); refresh(); });
  }, [refresh]);

  const trigger = async (key) => {
    const t = TRIGGER[key];
    if (!t) return;
    const isMag = MAG_KEYS.has(key);
    if (isMag && magRunning) return;
    if (!isMag && busyKey) return;
    if (CONFIRM[key] && !window.confirm(CONFIRM[key])) return;
    if (!isMag) setBusyKey(key);
    try {
      await api.post(t.url, t.body);
      if (isMag) {
        setMag((m) => ({ ...(m || {}), running: true, job: key, updated: 0, total: 0, commesse_done: 0, commesse_total: 0 }));
        wasRunning.current = true;
        toast.info(key === "giacenza" ? "Giacenza in caricamento…" : "Avviato in background — può richiedere alcuni minuti.");
      } else if (t.statusUrl) {
        toast.info("Avviato in background — può richiedere alcuni minuti.");
        setTimeout(() => pollVendite(t.statusUrl), 3000);
      } else {
        toast.success("Aggiornato");
        setBusyKey(null);
        refresh();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Avvio fallito");
      if (!isMag) setBusyKey(null);
    }
  };

  const rowState = (key) => {
    const isMag = MAG_KEYS.has(key);
    if (isMag) {
      const active = magRunning && (mag?.job === key || mag?.job === "full");
      let pct = null;
      if (active) {
        const ct = mag?.commesse_total || 0, cd = mag?.commesse_done || 0;
        const tot = mag?.total || 0, upd = mag?.updated || 0;
        if (ct > 0 && cd < ct) pct = Math.round((cd / ct) * 100);                       // fase 1: scansione commesse
        else if (tot > 0 && (upd > 0 || (ct > 0 && cd >= ct))) pct = Math.round((upd / tot) * 100); // fase 2: aggiorno lo stock
        else if (ct > 0) pct = Math.round((cd / ct) * 100);
      }
      return { active, pct, msg: mag?.message };
    }
    return { active: busyKey === key, pct: null, msg: "In corso…" };
  };

  return (
    <div className="rounded-md border border-border bg-muted/20 px-3 py-2" data-testid="sync-status-panel">
      <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
        <Activity className="h-3.5 w-3.5 text-primary" />Stato sincronizzazioni
        {data === null && <Loader2 className="ml-1 h-3 w-3 animate-spin text-muted-foreground" />}
      </div>
      {data === false ? (
        <div className="mt-1 text-xs text-destructive">Impossibile leggere lo stato.</div>
      ) : data ? (
        <>
        {val && (
          <div className="mb-2 rounded border border-primary/30 bg-primary/5 px-2 py-1.5 text-[11px]" data-testid="magazzino-valore">
            {val.fiscale && val.fiscale.valore_costo != null ? (
              <span data-testid="magazzino-valore-fiscale">
                <span className="font-semibold text-foreground">Valore magazzino (CSV fiscale):</span>{" "}
                <span className="font-semibold text-foreground">€ {Math.round(val.fiscale.valore_costo).toLocaleString("it-IT")}</span> a costo
                <span className="text-muted-foreground"> · {val.fiscale.articoli} art.{val.fiscale.data ? ` · al ${val.fiscale.data}` : ""}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">Valore magazzino fiscale non ancora disponibile</span>
            )}
            {isAdmin && (
              <div className="mt-1.5 flex items-center gap-2">
                <button
                  type="button"
                  onClick={caricaNottePrima}
                  disabled={magRunning}
                  title="Carica la giacenza dallo snapshot CSV della notte prima (fallback se il magazzino di oggi è errato)"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-1 text-[10px] font-bold text-primary transition-colors hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="giacenza-notte-prima-btn">
                  {magRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <History className="h-3 w-3" />}
                  Carica giacenza notte prima
                </button>
                <span className="text-[10px] text-muted-foreground">se il magazzino di oggi è sbagliato</span>
              </div>
            )}
          </div>
        )}
        <div className="mt-2 grid grid-cols-1 gap-1.5 lg:grid-cols-2">
          {(data.items || []).map((it) => {
            const { active, pct, msg } = rowState(it.key);
            const isMag = MAG_KEYS.has(it.key);
            const showBtn = isAdmin && !!TRIGGER[it.key] && (it.key === "dettaglio" || it.level === "red" || it.level === "yellow" || active);
            const btnCls = (it.level === "yellow" && !active)
              ? "border-amber-500/50 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400"
              : "border-red-500/40 bg-red-500/10 text-red-500 hover:bg-red-500/20";
            return (
              <div key={it.key} className="flex items-start gap-2 rounded border border-border/60 bg-background/60 px-2 py-1.5" data-testid={`sync-${it.key}`}>
                <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${active ? "bg-amber-500 animate-pulse" : (DOT[it.level] || DOT.info)}`} title={active ? "In corso" : LBL[it.level]} />
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold text-foreground">{it.label}</div>
                  {DESC[it.key] && <div className="mt-0.5 text-[10px] leading-snug text-muted-foreground">{DESC[it.key]}</div>}
                  {it.reason && !active && (
                    <div className="mt-1 flex items-start gap-1 rounded bg-amber-500/10 px-1.5 py-1 text-[10px] font-semibold leading-snug text-amber-600 dark:text-amber-400" data-testid={`sync-reason-${it.key}`}>
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{it.reason}
                    </div>
                  )}
                  {active ? (
                    <>
                      <div className="mt-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400" data-testid={`sync-progress-${it.key}`}>
                        In corso: {msg || "avvio…"}{pct != null ? ` · ${pct}%` : ""}
                      </div>
                      <Bar pct={pct} />
                    </>
                  ) : (
                    <div className="mt-0.5 text-[10px] font-medium text-muted-foreground/80">Ultima esecuzione: {fmt(it.last)}</div>
                  )}
                </div>
                {showBtn && (
                  <button
                    type="button"
                    onClick={() => trigger(it.key)}
                    disabled={active || (isMag && magRunning) || (!isMag && !!busyKey)}
                    title={TRIGGER[it.key].title}
                    className={`ml-auto mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${btnCls}`}
                    data-testid={`sync-run-${it.key}`}>
                    {active ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3 fill-current" />}
                    {active ? "In corso…" : "Avvia"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-2 rounded border border-border/60 bg-background/60" data-testid="sync-istruzioni">
          <button
            type="button"
            onClick={() => setIstrOpen((o) => !o)}
            className="flex w-full items-center gap-1.5 px-2 py-1.5 text-[11px] font-semibold text-foreground transition-colors hover:bg-muted/40"
            data-testid="sync-istruzioni-toggle">
            <BookOpen className="h-3.5 w-3.5 text-primary" /> Istruzioni
            <ChevronDown className={`ml-auto h-3.5 w-3.5 transition-transform ${istrOpen ? "rotate-180" : ""}`} />
          </button>
          {istrOpen && (
            <div className="space-y-2 border-t border-border/60 px-3 py-2 text-[11px] leading-snug text-muted-foreground" data-testid="sync-istruzioni-body">
              <div className="font-semibold text-foreground">Note pratiche</div>
              <p>In automatico è già tutto orchestrato: Elenco commesse ogni notte (02:00) e ogni ora (:30); Dettaglio + Giacenza ogni notte (21:40); Ordini/preventivi ogni ora (:30). Quindi al mattino il dato è già coerente.</p>
              <p>Se il magazzino di oggi è sbagliato: usa "Carica giacenza notte prima" (fallback), poi rilancia solo la Giacenza normale quando il CSV odierno è a posto.</p>
              <p>Il pulsante "Aggiorna" in alto rilancia il ciclo commesse; per l'ATP di magazzino corretto affidati comunque alla sequenza 4 → 5.</p>
            </div>
          )}
        </div>
        </>
      ) : null}
    </div>
  );
}
